import { createSlice } from '@reduxjs/toolkit';

/**
 * API Cache Slice for storing frequently accessed data
 * Reduces redundant API calls for static/semi-static data
 */

const initialState = {
    // Summary counts for dashboard
    membersSummary: null,
    membersSummaryTimestamp: null,
    
    // Static data caches
    faqs: null,
    faqsTimestamp: null,
    
    banners: null,
    bannersTimestamp: null,
    
    occupations: null,
    occupationsTimestamp: null,
    
    stages: null,
    stagesTimestamp: null,
    
    roles: null,
    rolesTimestamp: null,
    
    // Cache TTLs in milliseconds
    cacheTTL: {
        membersSummary: 30000,  // 30 seconds
        faqs: 300000,          // 5 minutes
        banners: 300000,       // 5 minutes
        occupations: 600000,   // 10 minutes
        stages: 600000,        // 10 minutes
        roles: 600000,         // 10 minutes
    }
};

export const apiCacheSlice = createSlice({
    name: 'apiCache',
    initialState,
    reducers: {
        setMembersSummary: (state, { payload }) => {
            state.membersSummary = payload;
            state.membersSummaryTimestamp = Date.now();
        },
        setFaqs: (state, { payload }) => {
            state.faqs = payload;
            state.faqsTimestamp = Date.now();
        },
        setBanners: (state, { payload }) => {
            state.banners = payload;
            state.bannersTimestamp = Date.now();
        },
        setOccupations: (state, { payload }) => {
            state.occupations = payload;
            state.occupationsTimestamp = Date.now();
        },
        setStages: (state, { payload }) => {
            state.stages = payload;
            state.stagesTimestamp = Date.now();
        },
        setRoles: (state, { payload }) => {
            state.roles = payload;
            state.rolesTimestamp = Date.now();
        },
        clearCache: (state, { payload }) => {
            if (payload) {
                // Clear specific cache
                state[payload] = null;
                state[`${payload}Timestamp`] = null;
            } else {
                // Clear all caches
                return initialState;
            }
        },
        invalidateCache: (state, { payload }) => {
            // Mark cache as stale by setting timestamp to 0
            if (payload) {
                state[`${payload}Timestamp`] = 0;
            }
        }
    }
});

export const {
    setMembersSummary,
    setFaqs,
    setBanners,
    setOccupations,
    setStages,
    setRoles,
    clearCache,
    invalidateCache
} = apiCacheSlice.actions;

// Selectors with cache validity check
export const selectMembersSummary = (state) => {
    const { membersSummary, membersSummaryTimestamp, cacheTTL } = state.apiCache;
    if (!membersSummaryTimestamp) return null;
    if (Date.now() - membersSummaryTimestamp > cacheTTL.membersSummary) return null;
    return membersSummary;
};

export const selectFaqs = (state) => {
    const { faqs, faqsTimestamp, cacheTTL } = state.apiCache;
    if (!faqsTimestamp) return null;
    if (Date.now() - faqsTimestamp > cacheTTL.faqs) return null;
    return faqs;
};

export const selectBanners = (state) => {
    const { banners, bannersTimestamp, cacheTTL } = state.apiCache;
    if (!bannersTimestamp) return null;
    if (Date.now() - bannersTimestamp > cacheTTL.banners) return null;
    return banners;
};

export const selectOccupations = (state) => {
    const { occupations, occupationsTimestamp, cacheTTL } = state.apiCache;
    if (!occupationsTimestamp) return null;
    if (Date.now() - occupationsTimestamp > cacheTTL.occupations) return null;
    return occupations;
};

export const selectStages = (state) => {
    const { stages, stagesTimestamp, cacheTTL } = state.apiCache;
    if (!stagesTimestamp) return null;
    if (Date.now() - stagesTimestamp > cacheTTL.stages) return null;
    return stages;
};

export const selectRoles = (state) => {
    const { roles, rolesTimestamp, cacheTTL } = state.apiCache;
    if (!rolesTimestamp) return null;
    if (Date.now() - rolesTimestamp > cacheTTL.roles) return null;
    return roles;
};

export default apiCacheSlice.reducer;
